import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Workflow, LifeBuoy, CheckCircle2, BarChart3, } from 'lucide-react';
import Layout from '../layout/Layout';
import {
  FiLifeBuoy,
  FiHelpCircle,
  FiCheckCircle,
  FiArrowUpRight,
  FiMonitor,
  FiArrowRight,
} from 'react-icons/fi';
const TYPEWRITER_WORDS = [
  'Web Applications',
  'Mobile Apps',
  'Digital Marketing',
  'DevOps Solutions',
  'Software Training',
  'Tech Consulting',
];

function useTypewriter(words, speed = 80, pause = 1800) {
  const [display, setDisplay] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIdx];
    let timeout;
    if (!deleting && charIdx < current.length) {
      timeout = setTimeout(() => setCharIdx((c) => c + 1), speed);
    } else if (!deleting && charIdx === current.length) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIdx > 0) {
      timeout = setTimeout(() => setCharIdx((c) => c - 1), speed / 2);
    } else {
      setDeleting(false);
      setWordIdx((w) => (w + 1) % words.length);
    }
    setDisplay(current.slice(0, charIdx));
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, wordIdx, words, speed, pause]);

  return display;
}

/* ── Scroll-reveal hook: adds a class once an element enters the viewport ── */
function useRevealOnScroll(options = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px', ...options }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, revealed];
}

/* ── Count-up hook for the "Years of Experience" stat ── */
function useCountUp(target, revealed, duration = 1400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!revealed) return;
    let start;
    let frame;
    const step = (ts) => {
      if (start === undefined) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [revealed, target, duration]);

  return value;
}

/* ── Wrapper that fades/slides a section in on scroll, with optional stagger ──
   Accepts an optional `innerRef` if the parent also needs direct access to the node
   (e.g. to drive the count-up stat), merging it with the internal observer ref. */
function Reveal({ children, as: Tag = 'div', delay = 0, className = '', innerRef, style, ...rest }) {
  const [ref, revealed] = useRevealOnScroll();

  const setRefs = (node) => {
    ref.current = node;
    if (typeof innerRef === 'function') innerRef(node);
    else if (innerRef) innerRef.current = node;
  };

  return (
    <Tag
      ref={setRefs}
      className={`haz-reveal ${revealed ? 'haz-revealed' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ── Wraps content in a subtle 3D tilt that follows the cursor ── */
function TiltCard({ children, className = '', maxTilt = 8, style, ...rest }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setTilt({
      x: (py - 0.5) * -2 * maxTilt,
      y: (px - 0.5) * 2 * maxTilt,
    });
  };

  const handleLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div
      ref={ref}
      className={`haz-tilt ${className}`}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{
        transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

export default function Home() {
  const typed = useTypewriter(TYPEWRITER_WORDS);
  const [visible, setVisible] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);

  const [statRef, statRevealed] = useRevealOnScroll();
  const years = useCountUp(6, statRevealed);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      setScrollPct(scrollable > 0 ? (doc.scrollTop / scrollable) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Layout>

      {/* Scroll progress indicator */}
      <div className="haz-scroll-progress" aria-hidden="true">
        <div className="haz-scroll-progress-fill" style={{ width: `${scrollPct}%` }} />
      </div>

      {/* ─── HERO ─────────────────────────────────────────────────────── */}
      <section className="haz-hero">

        {/* Animated Brand Background */}
        <div className="haz-hero-bg" aria-hidden="true">
          <div className="haz-bg-orb haz-bg-orb-1" />
          <div className="haz-bg-orb haz-bg-orb-2" />
          <div className="haz-bg-orb haz-bg-orb-3" />
          <div className="haz-bg-grid" />
          <div className="haz-bg-aurora" />
          <div className="haz-bg-particles">
            {Array.from({ length: 14 }).map((_, i) => (
              <span
                key={i}
                className="haz-particle"
                style={{
                  left: `${(i * 137) % 100}%`,
                  animationDelay: `${i * 0.7}s`,
                  animationDuration: `${8 + (i % 5) * 2}s`,
                }}
              />
            ))}
          </div>
          <img src="banner-logo.png" alt="Logo" className="haz-bg-logo" />
        </div>

        {/* Top hero content – heading + CTA */}
        <div className="container haz-hero-top">
          <div className="haz-hero-content">

            {/* Animated badge */}
            <div
              className="haz-hero-badge"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(20px)', transition: 'opacity 0.7s 0.1s, transform 0.7s 0.1s' }}
            >
              <span className="haz-pulse-dot" />
              Your Trusted Technology Partner
            </div>

            {/* Main heading with char-split animation */}
           <h1 className="haz-hero-heading" data-text="Magizhini Technology Services">
  {"Magizhini Technology".split("").map((ch, i) => (
    <span
      key={`line1-${i}`}
      className="haz-char"
      style={{
        animationDelay: `${0.3 + i * 0.04}s`,
        display: ch === " " ? "inline" : "inline-block",
      }}
    >
      {ch === " " ? "\u00a0" : ch}
    </span>
  ))}

  <br />

  {"Services".split("").map((ch, i) => (
    <span
      key={`line2-${i}`}
      className="haz-char"
      style={{
        animationDelay: `${0.3 + (i + 20) * 0.04}s`,
        display: "inline-block",
      }}
    >
      {ch}
    </span>
  ))}
</h1>

            {/* Sub-heading typewriter */}
            <div
              className="haz-hero-sub"
              style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.8s 0.8s' }}
            >
              We Build&nbsp;
              <span className="haz-typed-text">
                {typed}<span className="haz-caret" />
              </span>
            </div>

            {/* CTA + review row */}
            <div
              className="haz-hero-actions"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(20px)', transition: 'opacity 0.8s 1s, transform 0.8s 1s' }}
            >
              <Link to="/services" className="haz-btn-primary">
                <span className="haz-btn-shine" aria-hidden="true" />
                <span className="haz-btn-label">Get Started</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M7 17L17 7M17 7H7M17 7v10" /></svg>
              </Link>
              <Link to="/contact" className="haz-btn-secondary">Contact Us</Link>
              <div className="haz-review-row">
                <div className="haz-avatar-stack">
                  {[
                    { src: '/assets/images/home-clientlogo/oriental.png', alt: 'Client Logo 1' },
                    { src: '/assets/images/home-clientlogo/orientalpay.png', alt: 'Client Logo 2' },
                    { src: '/assets/images/home-clientlogo/bringmeal.png', alt: 'Client Logo 3' },
                    
                  ].map((logo, n) => (
                    <Link key={logo.src} to="/our-clients" className="haz-avatar haz-client-logo" style={{ animationDelay: `${n * 0.15}s` }}>
                      <img src={logo.src} alt={logo.alt} />
                    </Link>
                  ))}
                </div>
                <span className="haz-review-text"><strong>500+</strong> Happy Clients</span>
              </div>
            </div>
          </div>
        </div>

        {/* Rotating service badge — pinned top-left */}
        <div className="haz-circle-badge-wrap">
          <div className="haz-circle-spinning">
            <img src="banner-logo.png" alt="Magizhini Tech" className="haz-spin-img" />
            <svg className="haz-spin-ring" viewBox="0 0 200 200">
              <path id="circlePath" d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0" fill="none"/>
              <text className="haz-spin-text">
                <textPath href="#circlePath" startOffset="0%">
                  Software Development • Mobile Apps • DevOps • Marketing •&nbsp;
                </textPath>
              </text>
            </svg>
          </div>
        </div>
      </section>

      {/* Scrolling ticker bar */}
      <div className="haz-ticker-bar">
        <div className="haz-ticker-track">
          {[
            'Software Development','Mobile App Development','Digital Marketing',
            'DevOps & Cloud','Software Training','Tech Consulting',
            'Software Development','Mobile App Development','Digital Marketing',
            'DevOps & Cloud','Software Training','Tech Consulting',
          ].map((item, i) => (
            <span key={i} className="haz-ticker-item">
              <span className="haz-ticker-star">✦</span>
              {item}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        /* ══════════════════════════════════════
           HERO
        ══════════════════════════════════════ */
        .haz-hero {
          position: relative;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: linear-gradient(135deg, #061428 0%, #0B1F3F 40%, #0d2a4a 70%, #082030 100%);
        }

        /* ── Animated brand background ── */
        .haz-hero-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
        }

        /* Floating teal orbs */
        .haz-bg-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
        }
        .haz-bg-orb-1 {
          width: 700px; height: 700px;
          top: -200px; left: -150px;
          background: radial-gradient(circle, rgba(0,165,181,0.3) 0%, transparent 70%);
          animation: hazOrbDrift1 16s ease-in-out infinite alternate;
        }
        .haz-bg-orb-2 {
          width: 500px; height: 500px;
          bottom: -100px; right: -80px;
          background: radial-gradient(circle, rgba(0,165,181,0.2) 0%, transparent 70%);
          animation: hazOrbDrift2 20s ease-in-out infinite alternate;
        }
        .haz-bg-orb-3 {
          width: 350px; height: 350px;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          background: radial-gradient(circle, rgba(0,196,216,0.12) 0%, transparent 70%);
          animation: hazOrbDrift3 12s ease-in-out infinite alternate;
        }
          @media (max-width: 480px) {
  .haz-hero-badge {
    font-size: 11px;
    letter-spacing: 0.08em;
    padding: 8px 16px;
    text-align: center;
    white-space: normal;
  }
  .haz-hero-heading {
    line-height: 1.15;
  }
  .haz-hero-top {
    padding-top: 100px;
    padding-bottom: 70px;
  }
}
        @keyframes hazOrbDrift1 {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(60px, 50px); }
        }
        @keyframes hazOrbDrift2 {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(-50px, -40px); }
        }
        @keyframes hazOrbDrift3 {
          0%   { transform: translate(-50%, -50%) scale(1); }
          100% { transform: translate(-50%, -50%) scale(1.4); }
        }

        /* Dot grid overlay */
        .haz-bg-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(0,165,181,0.15) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 50%, black 30%, transparent 100%);
          -webkit-mask-image: radial-gradient(ellipse 85% 85% at 50% 50%, black 30%, transparent 100%);
          animation: hazGridScroll 50s linear infinite;
        }
        @keyframes hazGridScroll {
          from { background-position: 0 0; }
          to   { background-position: 40px 80px; }
        }

        /* Aurora wave at bottom */
        .haz-bg-aurora {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 220px;
          background: linear-gradient(0deg,
            rgba(0,165,181,0.18) 0%,
            rgba(11,31,63,0.5) 40%,
            transparent 100%
          );
          animation: hazAuroraWave 8s ease-in-out infinite alternate;
        }
        @keyframes hazAuroraWave {
          0%   { opacity: 0.6; transform: scaleX(1); }
          100% { opacity: 1;   transform: scaleX(1.05); }
        }

        /* Floating particles drifting upward through the hero */
        .haz-bg-particles {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }
        .haz-particle {
          position: absolute;
          bottom: -20px;
          width: 4px; height: 4px;
          border-radius: 50%;
          background: rgba(0, 196, 216, 0.55);
          box-shadow: 0 0 8px rgba(0, 196, 216, 0.6);
          animation-name: hazParticleFloat;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        @keyframes hazParticleFloat {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          10%  { opacity: 0.8; }
          90%  { opacity: 0.4; }
          100% { transform: translateY(-620px) translateX(30px); opacity: 0; }
        }

        /* Oversized, faint logo watermark — the real brand mark anchoring the hero */
        .haz-bg-logo {
          position: absolute;
          top: 50%; right: -6%;
          transform: translateY(-50%);
          width: 640px;
          max-width: 60vw;
          opacity: 0.06;
          filter: saturate(0) brightness(2.2);
          pointer-events: none;
          animation: hazLogoFloat 10s ease-in-out infinite alternate;
        }
        @keyframes hazLogoFloat {
          0%   { transform: translateY(-50%) scale(1); }
          100% { transform: translateY(-53%) scale(1.03); }
        }
        @media (max-width: 767px) {
          .haz-bg-logo { display: none; }
        }

        /* ── Top hero content ── */
        .haz-hero-top {
          position: relative;
          z-index: 2;
          padding-top: 140px;
          padding-bottom: 110px;
        }
        .haz-hero-content {
          max-width: 820px;
          margin: 0 auto;
          text-align: center;
        }

        /* Badge */
        .haz-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(0,165,181,0.12);
          border: 1px solid rgba(0,165,181,0.35);
          color: #00A5B5;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          line-height: 1.2;
          padding: 10px 22px;
          border-radius: 50px;
          margin-bottom: 28px;
        }
        .haz-pulse-dot {
          display: inline-block;
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #00A5B5;
          animation: hazPulseDot 1.8s ease-in-out infinite;
        }
        @keyframes hazPulseDot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,165,181,0.8); }
          50%       { box-shadow: 0 0 0 7px rgba(0,165,181,0); }
        }

        /* Big heading with char-split reveal */
        .haz-hero-heading {
  font-size: 75px;
  font-weight: 900;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 20px;
  letter-spacing: -0.03em;
}

@media (max-width: 1024px) {
  .haz-hero-heading {
    font-size: 60px;
  }
}

@media (max-width: 768px) {
  .haz-hero-heading {
    font-size: 48px;
    line-height: 1.2;
  }
}

@media (max-width: 576px) {
  .haz-hero-heading {
    font-size: 32px;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }
}

@media (max-width: 375px) {
  .haz-hero-heading {
    font-size: 32px;
  }
}
        .haz-char {
          opacity: 0;
          transform: translateY(100%);
          animation: hazCharReveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @keyframes hazCharReveal {
          from { opacity: 0; transform: translateY(80%) rotateX(-40deg); }
          to   { opacity: 1; transform: translateY(0) rotateX(0deg); }
        }

        /* Sub-heading typewriter */
        .haz-hero-sub {
          font-size: 20px;
          color: rgba(255,255,255,0.75);
          font-weight: 500;
          margin-bottom: 36px;
          min-height: 32px;
        }
        .haz-typed-text { color: #00A5B5; font-weight: 700; }
        .haz-caret {
          display: inline-block;
          width: 2px; height: 1em;
          background: #00A5B5;
          margin-left: 2px;
          vertical-align: text-bottom;
          animation: hazCaretBlink 0.75s step-end infinite;
        }
        @keyframes hazCaretBlink { 0%,100%{opacity:1} 50%{opacity:0} }

        /* CTA row */
        .haz-hero-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        .haz-btn-primary {
          position: relative;
          overflow: hidden;
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, #00A5B5, #00d4ea);
          color: #fff; font-weight: 700; font-size: 14px;
          padding: 15px 32px; border-radius: 50px; text-decoration: none;
          box-shadow: 0 8px 28px rgba(0,165,181,0.45);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .haz-btn-primary:hover { transform: translateY(-3px) scale(1.03); box-shadow: 0 14px 36px rgba(0,165,181,0.6); color:#fff; }
        .haz-btn-primary:hover svg { animation: hazArrowNudge 0.6s ease-in-out; }
        .haz-btn-label, svg { position: relative; z-index: 1; }
        .haz-btn-shine {
          position: absolute;
          top: 0; left: -60%;
          width: 40%; height: 100%;
          background: linear-gradient(120deg, transparent, rgba(255,255,255,0.55), transparent);
          transform: skewX(-20deg);
          animation: hazShineSweep 3.2s ease-in-out infinite;
        }
        @keyframes hazShineSweep {
          0%   { left: -60%; }
          40%  { left: 130%; }
          100% { left: 130%; }
        }
        @keyframes hazArrowNudge {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(3px, -3px); }
        }
        .haz-btn-secondary {
          display: inline-flex; align-items: center; gap: 8px;
          background: transparent; color: rgba(255,255,255,0.8);
          font-weight: 600; font-size: 14px; padding: 14px 32px;
          border-radius: 50px; text-decoration: none;
          border: 1.5px solid rgba(255,255,255,0.25);
          transition: border-color 0.2s, background 0.2s, color 0.2s, transform 0.2s;
        }
        .haz-btn-secondary:hover { border-color: #00A5B5; background: rgba(0,165,181,0.1); color: #fff; transform: translateY(-3px); }
        .haz-review-row { display: flex; align-items: center; gap: 10px; }
        .haz-avatar-stack { display: flex; }
        .haz-avatar {
          width: 78px; height: 78px; border-radius: 50%;
          border: 2px solid rgba(0,165,181,0.5);
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          background: #fff;
          margin-left: -18px;
          padding: 10px;
          opacity: 0;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          animation: hazAvatarPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 1.4s forwards;
        }
        .haz-avatar img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }
        .haz-avatar:hover {
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
          z-index: 2;
        }
        .haz-avatar:first-child { margin-left: 0; }
        @keyframes hazAvatarPop {
          from { opacity: 0; transform: scale(0.4); }
          to   { opacity: 1; transform: scale(1); }
        }
        .haz-review-text { font-size: 13px; color: rgba(255,255,255,0.7); }
        .haz-review-text strong { color: #00A5B5; }

        /* Rotating badge circle — pinned to the top-left corner of the hero */
        .haz-circle-badge-wrap {
          position: absolute;
          top: 130px;
          left: 5%;
          z-index: 2;
        }
        .haz-circle-spinning {
          position: relative;
          width: 200px; height: 200px;
          display: flex; align-items: center; justify-content: center;
        }
        .haz-spin-ring {
          position: absolute;
          inset: 0;
          animation: hazSpinRing 18s linear infinite;
        }
        @keyframes hazSpinRing { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .haz-spin-text {
          fill: rgba(255,255,255,0.55);
          font-size: 14px;
          letter-spacing: 0.08em;
          font-weight: 600;
          text-transform: uppercase;
        }
        .haz-spin-img {
          position: absolute;
          width: 85px; height: 85px;
          object-fit: contain;
          border-radius: 50%;
          background: rgba(0,165,181,0.15);
          border: 2px solid rgba(0,165,181,0.5);
          padding: 8px;
          box-shadow: 0 0 30px rgba(0,165,181,0.3);
          animation: hazImgPulse 3s ease-in-out infinite alternate, hazImgFloat 4s ease-in-out infinite;
        }
        @keyframes hazImgPulse {
          0%   { box-shadow: 0 0 20px rgba(0,165,181,0.25); }
          100% { box-shadow: 0 0 45px rgba(0,165,181,0.55); }
        }
        @keyframes hazImgFloat {
          0%, 100% { margin-top: 0; }
          50%      { margin-top: -6px; }
        }
        @media (max-width: 1199px) {
          .haz-circle-badge-wrap { display: none; }
        }

        /* ══════════════════════════════════════
           SCROLLING TICKER
        ══════════════════════════════════════ */
        .haz-ticker-bar {
          background: #00A5B5;
          overflow: hidden;
          padding: 14px 0;
          position: relative;
          z-index: 5;
        }
        .haz-ticker-track {
          display: flex;
          width: max-content;
          animation: hazTicker 30s linear infinite;
        }
        .haz-ticker-bar:hover .haz-ticker-track {
          animation-play-state: paused;
        }
        @keyframes hazTicker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .haz-ticker-item {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0 28px;
          white-space: nowrap;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .haz-ticker-item:hover {
          transform: scale(1.08);
        }
        .haz-ticker-star {
          color: rgba(255,255,255,0.6);
          font-size: 10px;
          display: inline-block;
          animation: hazStarSpin 3s linear infinite;
        }
        @keyframes hazStarSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* ══════════════════════════════════════
           SHARED SECTION SYSTEM (used below the fold)
        ══════════════════════════════════════ */
        .haz-eyebrow {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          padding: 7px 18px 9px;
          border-radius: 50px;
          margin-bottom: 18px;
          animation: hazEyebrowIn 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes hazEyebrowIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.9); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .haz-eyebrow::after {
          content: '';
          position: absolute;
          bottom: 3px; left: 18px;
          width: 14px; height: 2px;
          background: #D52B1E;
          border-radius: 2px;
          animation: hazEyebrowDash 2.4s ease-in-out infinite;
        }
        @keyframes hazEyebrowDash {
          0%, 100% { width: 14px; }
          50%      { width: 22px; }
        }
        .haz-eyebrow--light { background: rgba(0,165,181,0.1); color: #00A5B5; }
        .haz-eyebrow--dark  { background: rgba(0,165,181,0.18); color: #4DD8E0; }

        .haz-card {
          border-radius: 18px;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .haz-card:hover { transform: translateY(-6px); }
        .haz-card:hover .haz-icon-float {
          animation-play-state: paused;
          transform: scale(1.12) rotate(-6deg);
        }

        /* Gentle continuous float for circular icon badges inside cards */
        .haz-icon-float {
          animation: hazIconFloat 3.4s ease-in-out infinite;
          transition: transform 0.3s ease;
        }
        @keyframes hazIconFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-5px); }
        }

        /* Scroll-reveal utility — fades and slides content up into place */
        .haz-reveal {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .haz-reveal.haz-revealed {
          opacity: 1;
          transform: translateY(0);
        }
        @media (prefers-reduced-motion: reduce) {
          .haz-reveal { opacity: 1; transform: none; transition: none; }
        }

        /* Checklist items slide in from the left, one after another */
        .haz-check-item {
          opacity: 0;
          transform: translateX(-14px);
          animation: hazCheckIn 0.5s ease forwards;
        }
        @keyframes hazCheckIn {
          to { opacity: 1; transform: translateX(0); }
        }
        .haz-check-item:hover { transform: translateX(4px); }
        .haz-check-item {
          transition: transform 0.25s ease;
        }
        .haz-check-icon-pop {
          display: inline-flex;
          transform: scale(0);
          animation: hazCheckIconPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        @keyframes hazCheckIconPop {
          0%   { transform: scale(0) rotate(-90deg); }
          70%  { transform: scale(1.25) rotate(8deg); }
          100% { transform: scale(1) rotate(0deg); }
        }

        /* ══════════════════════════════════════
           WHY CHOOSE US — accent-driven cards
        ══════════════════════════════════════ */
        .haz-why-card {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(16, 34, 60, 0.05);
          transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease, border-color 0.35s ease;
        }
        .haz-why-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent, color-mix(in srgb, var(--haz-accent) 8%, transparent), transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
          pointer-events: none;
        }
        .haz-why-card:hover::before {
          transform: translateX(100%);
        }
        .haz-why-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 22px 40px rgba(16, 34, 60, 0.12);
          border-color: color-mix(in srgb, var(--haz-accent) 35%, transparent);
        }
        .haz-why-index {
          position: absolute;
          top: 14px; right: 20px;
          font-size: 34px;
          font-weight: 800;
          color: color-mix(in srgb, var(--haz-accent) 12%, transparent);
          line-height: 1;
          user-select: none;
          transition: color 0.35s ease, transform 0.35s ease;
        }
        .haz-why-card:hover .haz-why-index {
          color: color-mix(in srgb, var(--haz-accent) 22%, transparent);
          transform: scale(1.1) rotate(-4deg);
        }
        .haz-why-icon-wrap {
          position: relative;
          width: 72px; height: 72px;
          margin: 0 auto 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transform: scale(0.4) rotate(-20deg);
          transition: opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s;
        }
        .haz-revealed .haz-why-icon-wrap {
          opacity: 1;
          transform: scale(1) rotate(0deg);
        }
        .haz-why-icon-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: color-mix(in srgb, var(--haz-accent) 10%, transparent);
          border: 1.5px solid color-mix(in srgb, var(--haz-accent) 25%, transparent);
          animation: hazRingPulse 3.6s ease-in-out infinite;
        }
        @keyframes hazRingPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 color-mix(in srgb, var(--haz-accent) 20%, transparent); }
          50%      { transform: scale(1.06); box-shadow: 0 0 0 8px transparent; }
        }
        .haz-why-card:hover .haz-why-icon-ring {
          animation-play-state: paused;
          transform: scale(1.12) rotate(8deg);
          background: color-mix(in srgb, var(--haz-accent) 16%, transparent);
        }
        .haz-why-icon {
          position: relative;
          z-index: 1;
          width: 30px; height: 30px;
          color: var(--haz-accent);
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .haz-why-card:hover .haz-why-icon {
          transform: scale(1.15) rotate(6deg);
        }
        .haz-why-underline {
          display: block;
          width: 28px;
          height: 3px;
          margin: 18px auto 0;
          border-radius: 2px;
          background: var(--haz-accent);
          transform: scaleX(1);
          transition: width 0.35s ease;
        }
        .haz-why-card:hover .haz-why-underline {
          width: 56px;
        }

        /* ══════════════════════════════════════
           SCROLL PROGRESS BAR
        ══════════════════════════════════════ */
        .haz-scroll-progress {
          position: fixed;
          top: 0; left: 0; right: 0;
          height: 3px;
          z-index: 999;
          background: rgba(0,0,0,0.05);
        }
        .haz-scroll-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #00A5B5, #00d4ea);
          transition: width 0.1s linear;
          box-shadow: 0 0 8px rgba(0,165,181,0.6);
        }

        /* ══════════════════════════════════════
           WORK PROCESS — connecting line + number badges
        ══════════════════════════════════════ */
        .haz-process-row {
          position: relative;
        }
        .haz-process-line {
          display: none;
        }
        @media (min-width: 768px) {
          .haz-process-line {
            display: block;
            position: absolute;
            top: 50px;
            left: 16.5%;
            right: 16.5%;
            height: 2px;
            background: linear-gradient(90deg, rgba(0,165,181,0.06), rgba(0,165,181,0.3) 20%, rgba(0,165,181,0.3) 80%, rgba(0,165,181,0.06));
            transform: scaleX(0);
            transform-origin: left center;
            transition: transform 1.1s cubic-bezier(0.22, 1, 0.36, 1) 0.3s;
            overflow: visible;
          }
          .haz-process-line::after {
            content: '';
            position: absolute;
            top: -3px;
            width: 8px; height: 8px;
            border-radius: 50%;
            background: #00A5B5;
            box-shadow: 0 0 10px rgba(0,165,181,0.7);
            animation: hazProcessDot 3.2s ease-in-out infinite;
          }
          .haz-process-row:has(.haz-revealed) .haz-process-line {
            transform: scaleX(1);
          }
        }
        @keyframes hazProcessDot {
          0%   { left: 0%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        .haz-process-card { overflow: visible; }
        .haz-process-num {
          position: absolute;
          top: -14px; left: 50%;
          transform: translateX(-50%) scale(0);
          width: 30px; height: 30px;
          border-radius: 50%;
          background: #00A5B5;
          color: #fff;
          font-size: 12px;
          font-weight: 800;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 12px rgba(0,165,181,0.4);
          animation: hazNumPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          transition: transform 0.3s ease;
        }
        .haz-process-card:hover .haz-process-num {
          transform: translateX(-50%) scale(1.15) rotate(-8deg);
        }
        @keyframes hazNumPop {
          to { transform: translateX(-50%) scale(1); }
        }
        .haz-process-icon {
          transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .haz-process-card:hover .haz-process-icon {
          transform: rotate(360deg) scale(1.1);
        }

        /* ══════════════════════════════════════
           SERVICE CARDS — shine sweep + accent bar
        ══════════════════════════════════════ */
        .haz-service-card {
          position: relative;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          opacity: 0;
          animation: hazServiceFadeIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @keyframes hazServiceFadeIn {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .haz-service-card:hover {
          transform: translateY(-6px) scale(1.02);
        }
        .haz-service-accent {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #00A5B5, #00d4ea);
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.4s ease;
        }
        .haz-service-card:hover .haz-service-accent {
          transform: scaleX(1);
        }
        .haz-service-shine {
          position: absolute;
          top: 0; left: -75%;
          width: 50%; height: 100%;
          background: linear-gradient(120deg, transparent, rgba(0,165,181,0.12), transparent);
          transform: skewX(-20deg);
          pointer-events: none;
        }
        .haz-service-card:hover .haz-service-shine {
          animation: hazShineSweep 0.9s ease;
        }
        .haz-service-icon-wrap {
          transition: transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .haz-service-card:hover .haz-service-icon-wrap {
          transform: rotate(-360deg);
        }

        /* ══════════════════════════════════════
           ABOUT SECTION — tilt image + floating badge
        ══════════════════════════════════════ */
        .haz-tilt {
          transition: transform 0.15s ease-out;
          transform-style: preserve-3d;
          will-change: transform;
        }
        .haz-about-tilt img {
          display: block;
          transition: transform 0.5s ease;
        }
        .haz-about-tilt:hover img {
          transform: scale(1.04);
        }
        .haz-about-image-reveal {
          clip-path: inset(0 100% 0 0);
          transition: clip-path 1s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .haz-revealed .haz-about-image-reveal {
          clip-path: inset(0 0% 0 0);
        }
        .haz-about-badge {
          animation: hazBadgeFloat 4.5s ease-in-out infinite;
          transition: transform 0.3s ease;
        }
        .haz-about-badge:hover {
          transform: scale(1.08) rotate(4deg);
        }
        @keyframes hazBadgeFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-8px) rotate(-2deg); }
        }
        .haz-about-feature {
          transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .haz-about-feature:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(16,34,60,0.08);
        }
        .haz-learn-link {
          position: relative;
        }
        .haz-learn-link::after {
          content: '';
          position: absolute;
          left: 0; bottom: -3px;
          width: 0; height: 2px;
          background: currentColor;
          transition: width 0.3s ease;
        }
        .haz-learn-link:hover::after {
          width: 100%;
        }
        .haz-learn-link span {
          display: inline-block;
          transition: transform 0.3s ease;
        }
        .haz-learn-link:hover span {
          transform: translate(3px, -3px);
        }
          /* ══════════════════════════════════════
   FLOATING STAT CARDS (about image)
══════════════════════════════════════ */
.haz-float-card {
  z-index: 3;
  opacity: 0;
  transform: scale(0.4) translateY(20px);
}
.haz-revealed .haz-float-card--top {
  animation: hazFloatCardPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s forwards,
             hazFloatCardBob 4.5s ease-in-out 1.1s infinite;
}
.haz-revealed .haz-float-card--bottom {
  animation: hazFloatCardPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.9s forwards,
             hazFloatCardBob 4.5s ease-in-out 1.5s infinite;
}
@keyframes hazFloatCardPop {
  0%   { opacity: 0; transform: scale(0.3) translateY(24px) rotate(-6deg); }
  60%  { opacity: 1; transform: scale(1.08) translateY(-4px) rotate(1deg); }
  100% { opacity: 1; transform: scale(1) translateY(0) rotate(0deg); }
}
@keyframes hazFloatCardBob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
}
.haz-float-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.haz-float-card:hover {
  transform: translateY(-4px) scale(1.03);
  box-shadow: 0 16px 32px rgba(16,34,60,0.15);
}
.haz-float-icon-wrap {
  transition: transform 0.3s ease;
}
.haz-float-card:hover .haz-float-icon-wrap {
  transform: scale(1.1) rotate(-8deg);
}
@media (prefers-reduced-motion: reduce) {
  .haz-float-card { opacity: 1; transform: none; animation: none; }
}
      `}</style>

<section className="py-20 md:py-24 bg-white relative overflow-hidden">
        <div className="container">
          <div className="row items-center">
            <div className="col-xl-5">
              <Reveal className="relative mb-10 xl:mb-0">
  <TiltCard className="haz-about-tilt haz-about-image-reveal" maxTilt={6}>
    <img src="/image4 (1).jpg" alt="ontech" className="w-full rounded-2xl shadow-lg" />
  </TiltCard>

  {/* Floating stat card — top-left */}
  <div className="haz-float-card haz-float-card--top absolute -top-6 -left-6 bg-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 border border-gray-100">
    <span className="haz-float-icon-wrap w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
      <CheckCircle2 className="w-5 h-5 text-brand" strokeWidth={2} />
    </span>
    <div className="text-left">
      <span className="block text-lg font-bold font-heading text-brand-dark leading-none">100%</span>
      <span className="block text-xs text-gray-500 mt-0.5">On-Time Delivery</span>
    </div>
  </div>

  {/* Floating stat card — bottom-right */}
  <div className="haz-float-card haz-float-card--bottom absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 border border-gray-100">
    <span className="haz-float-icon-wrap w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
      <BarChart3 className="w-5 h-5 text-brand" strokeWidth={2} />
    </span>
    <div className="text-left">
      <span className="block text-lg font-bold font-heading text-brand-dark leading-none">98%</span>
      <span className="block text-xs text-gray-500 mt-0.5">Client Growth</span>
    </div>
  </div>

  {/* Small circular avatar badge */}
  <div className="absolute bottom-10 -left-4 w-20 h-20 rounded-full shadow-lg overflow-hidden border-4 border-white haz-about-badge z-10">
    <img src="/image.png" alt="ontech" className="w-full h-full object-cover" />
  </div>
</Reveal>
            </div>
            <div className="col-xl-7">
              <div className="xl:pl-12">
                <Reveal delay={100}>
                  <span className="haz-eyebrow haz-eyebrow--light">About Us</span>
                  <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-dark mb-5 leading-tight">
                    Premier Software Solutions and Tech Support
                  </h2>
                  <p className="text-gray-500 leading-relaxed mb-8">
                    Empower your business with our expert software development, training and consultancy services.
                  </p>
                </Reveal>
                <Reveal delay={200} className="row gutter-y-30 mb-8">
                  <div className="col-md-6">
                    <div className="haz-card haz-about-feature bg-gray-50 p-5 border border-gray-100 hover:border-brand/30">
                      <div className="haz-icon-float w-12 h-12 rounded-lg bg-brand flex items-center justify-center mb-3">
                         <FiLifeBuoy className="text-white text-xl" />
                        <span className="ontech-icons-two-buoy-help text-white text-xl"></span>
                      </div>
                      <h3 className="text-brand-dark font-bold font-heading mb-2">Customization</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">Unlocking customized innovative potential.</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="haz-card haz-about-feature bg-gray-50 p-5 border border-gray-100 hover:border-brand/30">
                      <div className=" w-12 h-12 rounded-lg bg-brand flex items-center justify-center mb-3" style={{ animationDelay: '0.3s' }}>
                         <FiHelpCircle className="text-white text-xl" />
                        <span className="ontech-icons-two-answer-call-question text-white text-xl"></span>
                      </div>
                      <h3 className="text-brand-dark font-bold font-heading mb-2">Tech Edge</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">Boundary-pushing tech evolution.</p>
                      
                    </div>
                    
                  </div>
                </Reveal>
                <Reveal delay={300}>
  <ul className="space-y-2 mb-8 list-none p-0">
    {[
      'Client-focused approach.',
      'Commitment to quality and innovation.',
      'Experience and expertise in the software industry.',
    ].map((item, i) => (
      <li key={i} className="haz-check-item flex items-center gap-3 text-brand-dark/80" style={{ animationDelay: `${0.5 + i * 0.15}s` }}>
        <FiCheckCircle
          className="haz-check-icon-pop text-brand flex-shrink-0"
          size={18}
          style={{ animationDelay: `${0.6 + i * 0.15}s` }}
        />
        {item}
      </li>
    ))}
  </ul>
  <Link
    className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-full hover:bg-brand-dark transition-colors haz-btn-primary"
    to="/about"
  >
    <span className="haz-btn-shine" aria-hidden="true" />
    <span className="haz-btn-label">Learn More</span> <FiArrowUpRight />
  </Link>
</Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Magizhini */}
      <section className="py-16 md:py-24 bg-white haz-why-section">
        <div className="container">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto">
            <span className="haz-eyebrow haz-eyebrow--light">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-dark">Why Magizhini Technology Services?</h2>
            <p className="text-gray-500 mt-4 leading-relaxed">
              We treat every engagement as a long-term relationship, not a one-off delivery — here's what that looks like in practice.
            </p>
          </Reveal>
          <div className="row gutter-y-30">
            {[
              { Icon: ShieldCheck, accent: '#00A5B5', title: 'Secure by Design', text: 'Every build is engineered with security and reliability baked in from day one, not bolted on after.' },
              { Icon: Workflow,    accent: '#3A7BD5', title: 'Clear, Predictable Process', text: 'You always know what stage your project is at — transparent timelines, no surprises.' },
              { Icon: LifeBuoy,    accent: '#D52B1E', title: 'Support That Doesn\u2019t Disappear', text: 'We stay reachable after launch, because software is a relationship, not a handoff.' },
            ].map(({ Icon, accent, title, text }, i) => (
              <div className="col-lg-4 col-md-6" key={i}>
                <Reveal
                  delay={i * 120}
                  className="haz-why-card bg-white p-8 text-center border border-gray-100"
                  style={{ '--haz-accent': accent }}
                >
                  <span className="haz-why-index">{`0${i + 1}`}</span>
                  <div className="haz-why-icon-wrap">
                    <span className="haz-why-icon-ring" />
                    <Icon className="haz-why-icon" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-brand-dark font-bold font-heading mb-2">{title}</h3>
                  <p className="text-brand-dark/70 leading-relaxed text-sm">{text}</p>
                  <span className="haz-why-underline" />
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Software Dev */}
      <section className="py-20 md:py-24 bg-gray-50 relative overflow-hidden">
        <div className="container">
          <div className="row items-center">
            <div className="col-xl-6">
              <Reveal className="relative mb-10 xl:mb-0">
                <div className="shape1 rotate-me absolute -top-6 -left-6 z-0">
                  <img src="/assets/images/favicons/magiTech.png" alt="Magizhini" className="w-16 h-16 opacity-20" />
                </div>
                <img src="image4 (2).webp" alt="#" className="w-full rounded-2xl shadow-lg relative z-10" />
              </Reveal>
            </div>
            <div className="col-xl-6">
              <div className="xl:pl-10">
                <Reveal delay={100}>
                  <span className="haz-eyebrow haz-eyebrow--light">Expert Software Development</span>
                  <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-dark mb-5 leading-tight">
                    We Bring Your Software Ideas to Life
                  </h2>
                  <p className="text-gray-500 leading-relaxed mb-8">
                    Our speciality is creating custom software solutions that increase productivity and boost profits for companies.
                  </p>
                </Reveal>
                <Reveal delay={200} className="space-y-5 mb-8">
                  <div className="flex items-start gap-4 haz-about-feature">
                    <div className="haz-icon-float flex-shrink-0 w-12 h-12 rounded-xl bg-brand flex items-center justify-center">
                      <span className="icon-buoy-help text-white text-xl"></span>
                    </div>
                    <div>
                      <h3 className="text-brand-dark font-bold font-heading mb-1">Embrace new ideas</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">Where cutting-edge development meets your unique vision seamlessly.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 haz-about-feature">
                    <div className="haz-icon-float flex-shrink-0 w-12 h-12 rounded-xl bg-brand flex items-center justify-center" style={{ animationDelay: '0.4s' }}>
                      <FiMonitor className="text-white text-xl" />
                      <span className="ontech-icons-two-computer text-white text-xl"></span>
                    </div>
                    <div>
                      <h3 className="text-brand-dark font-bold font-heading mb-1">Minimize downtime</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">Reliable, innovative solutions that keep your business running smoothly and efficiently.</p>
                    </div>
                  </div>
                </Reveal>
                <Reveal
                  delay={300}
                  innerRef={statRef}
                  className="bg-brand-dark rounded-2xl p-6 text-white relative overflow-hidden"
                  style={{ backgroundImage: 'url(/assets/images/shapes/support-experiance001.png)', backgroundSize: 'cover' }}
                >
                  <span className="haz-service-shine" aria-hidden="true" style={{ background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.15), transparent)' }} />
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-5xl font-bold text-brand leading-none">
                        {String(years).padStart(2, '0')}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold font-heading leading-tight">Years Of<br />Experience</h3>
                    </div>
                  </div>
                  <p className="text-white/70 text-sm mt-3 leading-relaxed">
                    We specialize in crafting innovative software solutions to tackle your toughest business challenges head-on.
                  </p>
                  <Link className="haz-learn-link inline-flex items-center gap-2 text-brand hover:text-white transition-colors mt-3 text-sm font-medium" to="/about">
                    Learn More <span className="ontech-icons-two-arrow-long"></span> <FiArrowUpRight />
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

     

      {/* Services */}
      <section className="py-20 md:py-24 bg-brand-dark relative overflow-hidden" style={{ backgroundImage: 'url(/assets/images/shapes/service-bg-04.png)', backgroundSize: 'cover' }}>
        <div className="container">
          <div className="row items-center mb-12">
            <div className="col-lg-6">
              <Reveal>
                <span className="haz-eyebrow haz-eyebrow--dark">Our Services</span>
                <h2 className="text-3xl md:text-4xl font-bold font-heading text-white leading-tight">
                  Crafting Code, Delivering Excellence.
                </h2>
              </Reveal>
            </div>
            <div className="col-lg-6">
              <Reveal delay={100} className="xl:pl-10 mt-4 xl:mt-0">
                <p className="text-white/60 leading-relaxed mb-4">Tailored solutions to meet your unique business needs, from design to deployment.</p>
                <Link className="haz-learn-link inline-flex items-center gap-2 text-brand hover:text-white transition-colors font-medium" to="/services">
                  Explore More <i className="ontech-icons-two-up-right-arrow"></i>
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
        <div className="container">
          <div
            className="services-four__carousel ontech-owl__carousel--with-shadow owl-carousel owl-theme thm-owl__carousel"
            data-owl-options='{"loop":true,"autoplay":true,"margin":30,"nav":false,"dots":false,"smartSpeed":500,"autoplayTimeout":5000,"responsive":{"0":{"items":1},"768":{"items":2},"992":{"items":3},"1440":{"items":4}}}'
          >
            {[
              { path: '/web-application',    icon: '/assets/images/icons/development.svg',       title: 'Web Application',    text: 'Revolutionize your online presence with our innovative web application, delivering seamless user experiences and unparalleled functionality.' },
              { path: '/mobile-application', icon: '/assets/images/icons/mobile-app.svg',        title: 'Mobile Application', text: 'Elevate your on-the-go experience with our intuitive mobile application, empowering you to stay connected and productive anytime, anywhere.' },
              { path: '/digital-marketing',  icon: '/assets/images/icons/digital-marketing.svg', title: 'Digital Marketing',  text: "Unlock your brand's full potential with our dynamic digital marketing strategies, fostering growth and interaction across online platforms." },
              { path: '/devops',             icon: '/assets/images/icons/devops.svg',            title: 'DevOps',             text: 'Maximize efficiency and collaboration through our DevOps solutions, streamlining development and accelerating deployment cycles.' },
              { path: '/software-training',  icon: '/assets/images/icons/trainer.svg',           title: 'Software Training',  text: 'Empower your team with expert-led software training tailored to your needs, unlocking their full potential and driving success in the digital age.' },
              { path: '/software-consulting',icon: '/assets/images/icons/discussion.svg',        title: 'Software Consulting',text: 'Navigate technology challenges confidently with our expert software consulting services designed to optimise and grow your business.' },
            ].map(({ path, icon, title, text }, i) => (
              <div className="item" key={path}>
                <div className="haz-card haz-service-card bg-white p-6 text-center hover:shadow-xl group" style={{ animationDelay: `${i * 100}ms` }}>
                  <span className="haz-service-accent" aria-hidden="true" />
                  <span className="haz-service-shine" aria-hidden="true" />
                  <div className="haz-icon-float haz-service-icon-wrap w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-brand transition-colors">
                    <img src={icon} className="w-8 h-8" alt={title} />
                  </div>
                  <h2 className="text-lg font-bold font-heading text-brand-dark mb-3">
                    <Link to={path} className="hover:text-brand transition-colors">{title}</Link>
                  </h2>
                  <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
       {/* Work Process */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto">
            <span className="haz-eyebrow haz-eyebrow--light">Our Work Process</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-dark">Three steps from idea to launch</h2>
          </Reveal>
          <div className="haz-process-row row gutter-y-30">
            <span className="haz-process-line" aria-hidden="true" />
            {[
              { icon: '/assets/images/icons/concept.svg', num: '01', title: 'Your Concept', text: 'We start by understanding your goals and mapping out what success looks like.' },
              { icon: '/assets/images/icons/task.svg',    num: '02', title: 'Build & Iterate', text: 'We break the work into clear milestones and keep you in the loop at every one.' },
              { icon: '/assets/images/icons/launch.svg',  num: '03', title: 'Launch & Support', text: 'We ship, then stay close to make sure it keeps running the way it should.' },
            ].map(({ icon, num, title, text }, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <Reveal delay={i * 150} className="haz-card haz-process-card bg-white border border-gray-100 shadow-sm hover:shadow-lg p-8 text-center relative">
                  <span className="haz-process-num" style={{ animationDelay: `${i * 150 + 250}ms` }}>{num}</span>
                  <div className="haz-icon-float w-20 h-20 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-5" style={{ animationDelay: `${i * 0.3}s` }}>
                    <img src={icon} className="w-10 h-10 haz-process-icon" alt={title} />
                  </div>
                  <h2 className="text-xl font-bold font-heading text-brand-dark mb-3">{title}</h2>
                  <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      

    </Layout>
  );
}



